import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  authForgotPassword,
  authMe,
  authResetPassword,
  authSignin,
  authSignup,
  authSignout,
  getAuthToken,
  setAuthToken,
  type AuthUser as ApiAuthUser,
} from '../lib/api';

export type AuthProviderId = 'email' | 'google' | 'apple' | 'facebook';

export interface User {
  id: string;
  email: string;
  username: string;
  displayName: string;
  avatar?: string;
  bio?: {
    en: string;
    ar: string;
  };
  city?: string;
  joinedAt: string;
  emailVerified: boolean;
  provider: AuthProviderId;
  preferences: {
    language: 'en' | 'ar';
    publicProfile: boolean;
    publicFavorites: boolean;
    notifyNewsletter: boolean;
    notifyDigest: boolean;
    notifyReplies: boolean;
  };
  connectedAccounts: AuthProviderId[];
}

interface AuthState {
  user: User | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  signUp: (params: {
    email: string;
    password: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  signInWithProvider: (provider: AuthProviderId) => Promise<{
    ok: boolean;
  }>;
  signOut: () => void;
  updateProfile: (updates: Partial<User>) => void;
  requestPasswordReset: (email: string) => Promise<{
    ok: boolean;
  }>;
  resetPassword: (
    token: string,
    newPassword: string,
  ) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  /** No-op — email verification uses `/auth/verify-email` + API. */
  verifyEmail: () => void;
  deleteAccount: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USERS_DB_KEY = 'ahg_users_db';

function loadUsersDB(): Record<
  string,
  {
    user: User;
    password: string;
  }
> {
  try {
    const raw = localStorage.getItem(USERS_DB_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveUsersDB(
  db: Record<
    string,
    {
      user: User;
      password: string;
    }
  >,
) {
  localStorage.setItem(USERS_DB_KEY, JSON.stringify(db));
}

function buildAvatar(seed: string): string {
  const name = encodeURIComponent(seed);
  return `https://ui-avatars.com/api/?name=${name}&background=C75C3F&color=fff&size=200&font-size=0.4&bold=true`;
}

function stableUsername(api: ApiAuthUser): string {
  const local =
    api.email
      .split('@')[0]
      ?.replace(/[^a-zA-Z0-9_-]/g, '')
      .toLowerCase() || 'user';
  return `${local}-${api.id.slice(-6)}`;
}

function apiUserToUser(api: ApiAuthUser): User {
  return {
    id: api.id,
    email: api.email,
    username: stableUsername(api),
    displayName: api.displayName,
    avatar: api.avatar ?? buildAvatar(api.displayName || api.email),
    bio: { en: '', ar: '' },
    city: 'Alexandria, Egypt',
    joinedAt: api.createdAt,
    emailVerified: api.emailVerified,
    provider: 'email',
    preferences: {
      language: 'en',
      publicProfile: true,
      publicFavorites: true,
      notifyNewsletter: true,
      notifyDigest: true,
      notifyReplies: true,
    },
    connectedAccounts: ['email'],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }
      try {
        const me = await authMe();
        if (!cancelled) setUser(apiUserToUser(me));
      } catch {
        setAuthToken(null);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn: AuthContextValue['signIn'] = async (email, password) => {
    try {
      const res = await authSignin(email, password);
      setAuthToken(res.token);
      setUser(apiUserToUser(res.user));
      return { ok: true };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'INVALID_CREDENTIALS') {
          return {
            ok: false,
            error:
              'INVALID_CREDENTIALS' /* caller maps bilingual */,
          };
        }
        if (e.code === 'RATE_LIMITED') {
          return { ok: false, error: 'RATE_LIMITED' };
        }
        return { ok: false, error: e.message };
      }
      return { ok: false, error: 'network' };
    }
  };

  const signUp: AuthContextValue['signUp'] = async ({
    email,
    password,
    displayName,
  }) => {
    try {
      const res = await authSignup({ email, password, displayName });
      setAuthToken(res.token);
      setUser(apiUserToUser(res.user));
      return { ok: true };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'EMAIL_EXISTS') {
          return { ok: false, error: 'EMAIL_EXISTS' };
        }
        if (e.code === 'RATE_LIMITED') {
          return { ok: false, error: 'RATE_LIMITED' };
        }
        return { ok: false, error: e.message };
      }
      return { ok: false, error: 'network' };
    }
  };

  const signInWithProvider: AuthContextValue['signInWithProvider'] =
    async (provider) => {
      await new Promise((r) => setTimeout(r, 700));
      const mockEmails: Record<AuthProviderId, string> = {
        email: '',
        google: 'visitor@gmail.com',
        apple: 'visitor@icloud.com',
        facebook: 'visitor@facebook.com',
      };
      const mockNames: Record<AuthProviderId, string> = {
        email: '',
        google: 'Alex Visitor',
        apple: 'Alex Visitor',
        facebook: 'Alex Visitor',
      };
      const email = mockEmails[provider];
      const displayName = mockNames[provider];
      const db = loadUsersDB();
      let record = db[email.toLowerCase()];
      if (!record) {
        const newUser: User = {
          id: `u_${Date.now()}`,
          email: email.toLowerCase(),
          username: `${email.split('@')[0]}-${Date.now().toString(36)}`,
          displayName,
          avatar: buildAvatar(displayName),
          bio: {
            en: '',
            ar: '',
          },
          city: 'Alexandria, Egypt',
          joinedAt: new Date().toISOString(),
          emailVerified: true,
          provider,
          preferences: {
            language: 'en',
            publicProfile: true,
            publicFavorites: true,
            notifyNewsletter: true,
            notifyDigest: true,
            notifyReplies: true,
          },
          connectedAccounts: [provider],
        };
        db[email.toLowerCase()] = {
          user: newUser,
          password: '__social__',
        };
        saveUsersDB(db);
        record = db[email.toLowerCase()];
      }
      setUser(record.user);
      return {
        ok: true,
      };
    };

  const signOut = () => {
    const token = getAuthToken();
    if (token) {
      void authSignout().finally(() => {
        setAuthToken(null);
        setUser(null);
      });
    } else {
      setAuthToken(null);
      setUser(null);
    }
  };

  const updateProfile: AuthContextValue['updateProfile'] = useCallback(
    (updates) => {
      setUser((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...updates };
        return next;
      });
    },
    [],
  );

  const requestPasswordReset: AuthContextValue['requestPasswordReset'] =
    async (email) => {
      try {
        await authForgotPassword(email);
      } catch {
        /* anti-enumeration: always ok */
      }
      return { ok: true };
    };

  const resetPassword: AuthContextValue['resetPassword'] = async (
    token,
    newPassword,
  ) => {
    try {
      await authResetPassword(token, newPassword);
      return { ok: true };
    } catch (e) {
      if (e instanceof ApiError && e.code === 'INVALID_RESET_TOKEN') {
        return { ok: false, error: 'INVALID_RESET_TOKEN' };
      }
      if (e instanceof ApiError) {
        return { ok: false, error: e.message };
      }
      return { ok: false, error: 'network' };
    }
  };

  const verifyEmail = () => {
    /* Legacy hook — verification is API-driven from VerifyEmailPage. */
  };

  const deleteAccount = () => {
    signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signInWithProvider,
        signOut,
        updateProfile,
        requestPasswordReset,
        resetPassword,
        verifyEmail,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
